import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import moment from "moment";
import type { LocationCapacityScheduleRow } from "../../../types/LocationCapacitySchedule";

interface CapacityScheduleEditorProps {
  rows: LocationCapacityScheduleRow[];
  onChange: (rows: LocationCapacityScheduleRow[]) => void;
  /** Fallback capacity (location.totalNumberOfSpaces / "Default Capacity") shown when no row covers a day. */
  fallbackCapacity: number | null;
  /**
   * Which part of the editor to render:
   * - `full` (default): the row table and the month preview together.
   * - `editor`: only the effective-dated row table + add button.
   * - `preview`: only the read-only month calendar preview.
   * Splitting lets the dialog place the table beside other controls and the
   * wide calendar full-width below them.
   */
  variant?: "full" | "editor" | "preview";
}

const emptyRow = (): LocationCapacityScheduleRow => ({
  id: null,
  effectiveDate: moment().format("YYYY-MM-DD"),
  endDate: moment().add(1, "day").format("YYYY-MM-DD"),
  capacity: 0,
  note: "",
});

/**
 * Resolve the capacity in effect on a given day: the covering row with the
 * latest effective date, mirroring the backend resolution logic.
 */
const resolveCapacity = (
  rows: LocationCapacityScheduleRow[],
  day: moment.Moment,
): number | null => {
  let best: LocationCapacityScheduleRow | null = null;
  rows.forEach((row) => {
    if (!row.effectiveDate) return;
    const start = moment(row.effectiveDate, "YYYY-MM-DD");
    if (!start.isValid()) return;
    if (day.isBefore(start, "day")) return;
    if (row.endDate && day.isAfter(moment(row.endDate, "YYYY-MM-DD"), "day")) return;
    if (!best || start.isAfter(moment(best.effectiveDate, "YYYY-MM-DD"), "day")) {
      best = row;
    }
  });
  return best ? (best as LocationCapacityScheduleRow).capacity : null;
};

/**
 * POC option (c): editable, effective-dated capacity rows plus a read-only
 * month preview that shows the resolved capacity per day. Lease changes map
 * naturally to "effective date → capacity" rows.
 */
const CapacityScheduleEditor = ({ rows, onChange, fallbackCapacity, variant = "full" }: CapacityScheduleEditorProps) => {
  const showEditor = variant !== "preview";
  const showPreview = variant !== "editor";
  // Which month the preview calendar is showing. Stored as an ISO month string
  // ("YYYY-MM") so navigation is just add/subtract a month.
  const [previewMonth, setPreviewMonth] = useState(moment().format("YYYY-MM"));

  const previewDays = useMemo(() => {
    const start = moment(previewMonth, "YYYY-MM").startOf("month");
    const daysInMonth = start.daysInMonth();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const day = moment(start).add(i, "days");
      const resolved = resolveCapacity(rows, day);
      // Uncovered days fall back to the location's default capacity, but are
      // flagged so the calendar can highlight them as the default (not a value
      // the supplier explicitly scheduled).
      const isDefault = resolved == null;
      const capacity = resolved ?? fallbackCapacity;
      return { day, capacity, isDefault };
    });
  }, [rows, fallbackCapacity, previewMonth]);

  const shiftMonth = (delta: number) => {
    setPreviewMonth(moment(previewMonth, "YYYY-MM").add(delta, "month").format("YYYY-MM"));
  };

  const updateRow = (index: number, patch: Partial<LocationCapacityScheduleRow>) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  // Setting the start date also defaults the end date to the next day when it is
  // empty, and bumps an end date that would otherwise fall before the new start.
  const handleEffectiveDateChange = (index: number, value: string) => {
    const row = rows[index];
    const patch: Partial<LocationCapacityScheduleRow> = { effectiveDate: value };
    if (value) {
      const start = moment(value, "YYYY-MM-DD");
      const end = row.endDate ? moment(row.endDate, "YYYY-MM-DD") : null;
      // Only correct an existing endDate that is now invalid or before the new
      // start. An intentionally cleared (open-ended) endDate is left untouched.
      if (end && (!end.isValid() || end.isBefore(start))) {
        patch.endDate = moment(start).add(1, "day").format("YYYY-MM-DD");
      }
    }
    updateRow(index, patch);
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  return (
    <Box>
      {showEditor && (
        <>
          <Typography variant="body2" sx={{ color: "#5E7782", mb: 1.5 }}>
            Enter the number of spaces available for each date range. Add a new row
            each time your capacity changes (for example when a lease starts or ends).
            Days without a schedule fall back to your location's default capacity,
            shown in blue on the calendar below.
          </Typography>

      <Table size="small" aria-label="Capacity schedule">
        <TableHead>
          <TableRow>
            <TableCell>Effective date</TableCell>
            <TableCell>End date</TableCell>
            <TableCell>Capacity</TableCell>
            <TableCell>Note</TableCell>
            <TableCell padding="none" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" sx={{ color: "#8299A5", py: 1 }}>
                  No capacity rows yet. Add one to start reflecting real capacity.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((row, index) => {
            const endBeforeStart = Boolean(
              row.effectiveDate &&
                row.endDate &&
                moment(row.endDate, "YYYY-MM-DD").isBefore(
                  moment(row.effectiveDate, "YYYY-MM-DD"),
                ),
            );
            return (
              <TableRow key={row.id ?? `new-${index}`}>
                <TableCell>
                  <TextField
                    type="date"
                    size="small"
                    value={row.effectiveDate ?? ""}
                    onChange={(e) => handleEffectiveDateChange(index, e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="date"
                    size="small"
                    value={row.endDate ?? ""}
                    onChange={(e) => updateRow(index, { endDate: e.target.value || null })}
                    InputLabelProps={{ shrink: true }}
                    // Native picker prevents choosing a day before the start date.
                    inputProps={{ min: row.effectiveDate ?? undefined }}
                    error={endBeforeStart}
                    helperText={endBeforeStart ? "End date can't be before the start date" : undefined}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    inputProps={{ min: 0, style: { width: 70 } }}
                    value={Number.isFinite(row.capacity) ? row.capacity : ""}
                    onChange={(e) =>
                      updateRow(index, { capacity: Math.max(0, Number(e.target.value)) })
                    }
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="e.g. Lease with ACME"
                    value={row.note ?? ""}
                    onChange={(e) => updateRow(index, { note: e.target.value })}
                  />
                </TableCell>
                <TableCell padding="none">
                  <Tooltip title="Remove row">
                    <IconButton aria-label="Remove row" size="small" onClick={() => removeRow(index)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onChange([...rows, emptyRow()])}
        sx={{ mt: 1 }}
      >
        Add capacity change
      </Button>
        </>
      )}

      {showPreview && (
        <>
      {/* Month preview — how buyers will see resolved capacity per day */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 3, mb: 1 }}>
        <Tooltip title="Previous month">
          <IconButton size="small" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="subtitle2">
          {moment(previewMonth, "YYYY-MM").format("MMMM YYYY")} preview
        </Typography>
        <Tooltip title="Next month">
          <IconButton size="small" aria-label="Next month" onClick={() => shiftMonth(1)}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
        }}
      >
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((weekday) => (
          <Typography
            key={weekday}
            sx={{
              fontSize: "0.6rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "#8299A5",
              textAlign: "center",
              pb: 0.5,
            }}
          >
            {weekday}
          </Typography>
        ))}
        {/* Leading blanks so day 1 lands under its real weekday */}
        {Array.from({ length: previewDays[0]?.day.day() ?? 0 }).map((_, i) => (
          <Box key={`blank-${i}`} />
        ))}
        {previewDays.map(({ day, capacity, isDefault }) => {
          // Default days (no schedule row) get a distinct blue tint + "default"
          // label so they're clearly the fallback, not an explicit value.
          const background = isDefault
            ? "#EAF1FB"
            : capacity == null
              ? "#F5F7F8"
              : capacity > 0
                ? "#EAF7EE"
                : "#FCEBEA";
          return (
            <Box
              key={day.format("YYYY-MM-DD")}
              sx={{
                border: isDefault ? "1px dashed #9BBDE8" : "1px solid #E3E9ED",
                borderRadius: 1,
                overflow: "hidden",
                backgroundColor: background,
              }}
            >
              {/* Day number — its own banded header, top-left */}
              <Box
                sx={{
                  px: 0.75,
                  py: 0.25,
                  backgroundColor: "rgba(255,255,255,0.65)",
                  borderBottom: isDefault ? "1px dashed #9BBDE8" : "1px solid #E3E9ED",
                }}
              >
                <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#8299A5" }}>
                  {day.format("D")}
                </Typography>
              </Box>
              {/* Capacity — large, centered, with a unit / default label */}
              <Box sx={{ textAlign: "center", py: 0.75 }}>
                <Typography
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    lineHeight: 1,
                    color: isDefault ? "#2C6BC4" : "#324559",
                  }}
                >
                  {capacity == null ? "—" : capacity}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.55rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: isDefault ? "#2C6BC4" : "#8299A5",
                  }}
                >
                  {isDefault ? "default" : "spaces"}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      <Typography variant="caption" sx={{ color: "#8299A5", display: "block", mt: 1 }}>
        Blue (dashed) days use your location's default capacity
        {fallbackCapacity != null ? ` (${fallbackCapacity})` : ""}; green/red days come from your schedule.
      </Typography>
        </>
      )}
    </Box>
  );
};

export default CapacityScheduleEditor;
